import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/contexts/theme-context'
import { PhaseStatsCards } from '@/features/student/dashboard/components/PhaseStatsCards'
import UnifiedProjectCard from '@/components/cards/UnifiedProjectCard'
import ProjectFilters from '@/components/filters/ProjectFilters'
import { getProjetos } from '@/api/queries'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, FolderOpen, ChevronLeft, ChevronRight, Sun, Moon, Rocket } from 'lucide-react'
import SectionLayout from '@/features/visitor/layout/SectionLayout'
import { PageBanner } from '@/components/common/PageBanner'
import HorizontalProjectFilters from '@/components/filters/HorizontalProjectFilters'
import Pagination from '@/components/ui/Pagination'
import { mapFaseToNumber, mapFaseNameToStatusKey } from '@/utils/phase-utils'

// Helper to transform project data
const transformarProjeto = (projeto: any) => {
    const autores = projeto.autores || []
    const lider = autores.find((a: any) => a.papel === 'LIDER') || autores[0]
    const equipe = autores.filter((a: any) => a.papel !== 'LIDER')

    // Helper para URL da imagem (usa /api proxy que funciona local, dev e prod)
    const getFullImageUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http')) return url;
        if (url.startsWith('/api/')) return url;
        const apiUrl = import.meta.env.VITE_API_URL || '/api';
        return `${apiUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    }

    return {
        id: projeto.uuid,
        uuid: projeto.uuid,
        nome: projeto.titulo,
        descricao: projeto.descricao,
        bannerUrl: getFullImageUrl(projeto.banner_url),
        faseAtual: mapFaseToNumber(projeto.fase_atual),
        fase_atual: projeto.fase_atual,
        // Improved mapping for filters
        curso: projeto.curso || projeto.curso_nome || projeto.departamento || 'Não informado',
        categoria: projeto.categoria || projeto.departamento || 'Geral',
        liderProjeto: lider ? { nome: lider.nome, email: lider.email || '' } : null,
        equipe: equipe.map((a: any) => ({ nome: a.nome })),
        orientadores: (projeto.orientadores || []).map((o: any) => ({ nome: o.nome })),
        tecnologias: (projeto.tecnologias || []).map((t: any) => t.nome),
        criadoEm: projeto.criado_em,
        publicadoEm: projeto.data_publicacao || projeto.publicado_em || projeto.criado_em,
        repositorio_url: projeto.repositorio_url,
        demo_url: projeto.demo_url,
        isOwner: false,
        // Added props for UnifiedProject type compatibility
        autorNome: lider ? lider.nome : 'Equipe do Projeto',
        status: 'PUBLISHED',
        visualizacoes: projeto.visualizacoes || 0,
        turma: projeto.turma || '',
        atualizadoEm: projeto.atualizado_em || projeto.criado_em,
        faseId: mapFaseToNumber(projeto.fase_atual),
        orientador: projeto.orientadores?.[0]?.nome,
        itinerario: projeto.itinerario,
        participouSaga: projeto.saga_senai || projeto.participou_saga,
        labMaker: projeto.senai_lab || projeto.lab_maker,
        participouEdital: projeto.participou_edital,
        ganhouPremio: projeto.ganhou_premio,
        // Adicionar status_fases vindo da API
        status_fases: projeto.status_fases || {},
        curtidas_count: projeto.curtidas_count || 0,
        visualizacoes_count: projeto.visualizacoes_count !== undefined ? projeto.visualizacoes_count : (projeto.visualizacoes || 0),
        ja_curtiu: projeto.ja_curtiu || false
    }
}

const ExplorerPage: React.FC = () => {
    const navigate = useNavigate()
    const { effectiveTheme } = useTheme()

    // Local state for filters
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCategoria, setSelectedCategoria] = useState<string | null>(null)

    const [selectedNivel, setSelectedNivel] = useState<string | null>(null)
    const [selectedStatusFase, setSelectedStatusFase] = useState<string | null>(null)
    const [apenasFaseAtual, setApenasFaseAtual] = useState<boolean>(true)
    const [selectedCurso, setSelectedCurso] = useState<string | null>(null)
    const [selectedDestaque, setSelectedDestaque] = useState<string | null>(null)
    const [sortOrder, setSortOrder] = useState<'A-Z' | 'Z-A' | 'novos' | 'antigos' | 'mais-vistos' | 'atualizados'>('atualizados')
    const [activePhaseSort, setActivePhaseSort] = useState<string | null>(null)

    // Pagination
    const [paginaAtual, setPaginaAtual] = useState(1)
    const itensPorPagina = 9

    // Fetch ALL projects for client-side filtering
    const { data, isLoading, error } = useQuery({
        queryKey: ['projetos-publicos-explorer'], // Removed search/page deps to cache all
        queryFn: () => getProjetos({
            limit: 1000, // Fetch all (or reasonable max)
            offset: 0
        }),
        staleTime: 60000, // Cache for 1 minute
        retry: 1
    })

    const projetosAPI = data?.projetos || []

    // Transform projects
    const projects = useMemo(() => {
        return projetosAPI.map(transformarProjeto)
    }, [projetosAPI])

    // Calculate stats based on ALL projects
    const projetosIdeacao = projects.filter(p => p.faseAtual === 1).length
    const projetosModelagem = projects.filter(p => p.faseAtual === 2).length
    const projetosPrototipagem = projects.filter(p => p.faseAtual === 3).length
    const projetosImplementacao = projects.filter(p => p.faseAtual === 4).length

    // Filter logic (Client-Side)
    const filteredProjects = useMemo(() => {
        let result = [...projects]

        // Search Term
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase()
            result = result.filter(p =>
                p.nome.toLowerCase().includes(lowerTerm) ||
                p.descricao?.toLowerCase().includes(lowerTerm)
            )
        }

        if (selectedCategoria) {
            result = result.filter(p => p.categoria === selectedCategoria)
        }

        if (selectedCurso) {
            // Normalizar para comparação (ignorar "Técnico em " e case)
            const cursoSelecionadoNorm = selectedCurso.replace('Técnico em ', '').trim().toLowerCase()

            result = result.filter(p => {
                const cursoProjeto = p.curso || ''
                const cursoProjetoNorm = cursoProjeto.replace('Técnico em ', '').trim().toLowerCase()

                // Tenta correspondência exata normalizada ou verifica se um contém o outro
                return cursoProjetoNorm === cursoSelecionadoNorm ||
                    cursoProjetoNorm.includes(cursoSelecionadoNorm) ||
                    cursoSelecionadoNorm.includes(cursoProjetoNorm)
            })
        }

        if (selectedNivel) {
            result = result.filter(p => {
                const nivelNum = mapFaseToNumber(selectedNivel)
                const faseProjeto = p.faseAtual
                const faseKey = mapFaseNameToStatusKey(selectedNivel)
                const statusFases = (p as any).status_fases || {}
                const faseStatus = statusFases[faseKey]?.status

                // Se "Apenas fase atual" está marcado, usar lógica antiga (filtrar apenas pela fase atual)
                if (apenasFaseAtual) {
                    const faseMatch = faseProjeto === nivelNum

                    // Se status da fase também foi selecionado, filtrar por ele
                    if (selectedStatusFase && faseMatch) {
                        return faseStatus === selectedStatusFase
                    }

                    return faseMatch
                }

                // Lógica nova: filtrar por status da fase específica, independente da fase atual
                // Se status da fase também foi selecionado, filtrar apenas por status da fase específica
                // Isso permite encontrar projetos em fases posteriores que já concluíram a fase filtrada
                if (selectedStatusFase) {
                    return faseStatus === selectedStatusFase
                }

                // Se apenas a fase foi selecionada (sem status), verificar se o projeto está nessa fase ou em fases posteriores
                // e se a fase filtrada tem algum conteúdo (não está pendente)
                if (faseStatus && faseStatus !== 'Pendente') {
                    return true
                }

                // Se não tem status definido, verificar pela fase atual do projeto
                return faseProjeto === nivelNum
            })
        }

        if (selectedDestaque) {
            switch (selectedDestaque) {
                case 'Itinerário':
                    result = result.filter(p => p.itinerario)
                    break
                case 'SENAI Lab':
                    result = result.filter(p => p.labMaker)
                    break
                case 'SAGA SENAI':
                    result = result.filter(p => p.participouSaga)
                    break
                case 'Edital':
                    result = result.filter(p => p.participouEdital)
                    break
                case 'Prêmio':
                    result = result.filter(p => p.ganhouPremio)
                    break
            }
        }

        // Sort
        switch (sortOrder) {
            case 'A-Z':
                result.sort((a, b) => a.nome.localeCompare(b.nome))
                break
            case 'Z-A':
                result.sort((a, b) => b.nome.localeCompare(a.nome))
                break
            case 'atualizados':
                result.sort((a, b) => new Date(b.atualizadoEm || 0).getTime() - new Date(a.atualizadoEm || 0).getTime())
                break
            case 'novos':
                result.sort((a, b) => new Date(b.criadoEm || 0).getTime() - new Date(a.criadoEm || 0).getTime())
                break
            case 'antigos':
                result.sort((a, b) => new Date(a.criadoEm || 0).getTime() - new Date(b.criadoEm || 0).getTime())
                break
            case 'mais-vistos':
                result.sort((a, b) => (b.visualizacoes || 0) - (a.visualizacoes || 0))
                break
        }

        if (activePhaseSort) {
            const targetPhaseNum = mapFaseToNumber(activePhaseSort)
            result.sort((a, b) => {
                const faseA = a.faseAtual || 1
                const faseB = b.faseAtual || 1
                const distA = (faseA - targetPhaseNum + 4) % 4
                const distB = (faseB - targetPhaseNum + 4) % 4
                return distA - distB
            })
        }

        return result
    }, [projects, searchTerm, selectedCategoria, selectedCurso, selectedNivel, selectedStatusFase, apenasFaseAtual, selectedDestaque, sortOrder, activePhaseSort])

    // Client-side Pagination
    const totalResultados = filteredProjects.length
    const totalPaginas = Math.ceil(totalResultados / itensPorPagina) || 1

    const paginatedProjects = useMemo(() => {
        const startIndex = (paginaAtual - 1) * itensPorPagina
        return filteredProjects.slice(startIndex, startIndex + itensPorPagina)
    }, [filteredProjects, paginaAtual])

    const handleSearchChange = (value: string) => {
        setSearchTerm(value)
        setPaginaAtual(1)
    }

    return (
        <SectionLayout>
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                {/* Top Banner */}
                <PageBanner
                    title="Explorar Vitrine"
                    subtitle="Navegue pelos projetos inovadores desenvolvidos no SENAI"
                    icon={<Rocket />}
                    color="blue"
                    className="pb-24" // Extra padding to allow significant overlap without hiding content
                />

                {/* Main Content Area with overlapping margin */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-20 relative z-20">

                    {/* Stats Cards */}
                    <div className="mb-8">
                        <PhaseStatsCards
                            projetosIdeacao={projetosIdeacao}
                            projetosModelagem={projetosModelagem}
                            projetosPrototipagem={projetosPrototipagem}
                            projetosImplementacao={projetosImplementacao}
                            onPhaseClick={(fase) => setActivePhaseSort(prev => prev === fase ? null : fase)}
                            activePhase={activePhaseSort}
                        />
                    </div>

                    {/* Horizontal Filters */}
                    <HorizontalProjectFilters
                        searchTerm={searchTerm}
                        setSearchTerm={handleSearchChange}
                        selectedCurso={selectedCurso}
                        setSelectedCurso={setSelectedCurso}
                        selectedCategoria={selectedCategoria}
                        setSelectedCategoria={setSelectedCategoria}
                        selectedNivel={selectedNivel}
                        setSelectedNivel={setSelectedNivel}
                        selectedStatusFase={selectedStatusFase}
                        setSelectedStatusFase={setSelectedStatusFase}
                        apenasFaseAtual={apenasFaseAtual}
                        setApenasFaseAtual={setApenasFaseAtual}
                        selectedDestaque={selectedDestaque}
                        setSelectedDestaque={setSelectedDestaque}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        totalResults={totalResultados}
                    />

                    {/* Project Grid */}
                    {isLoading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                <div key={n} className="h-96 bg-gray-200 dark:bg-gray-800 rounded-xl" />
                            ))}
                        </div>
                    ) : paginatedProjects.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedProjects.map((projeto) => (
                                <UnifiedProjectCard
                                    key={projeto.id}
                                    project={projeto}
                                    isGuest={true}
                                    onClick={() => navigate(`/vitrine/${projeto.uuid}`)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full inline-block mb-4">
                                <AlertCircle className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Nenhum projeto encontrado
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                                Tente ajustar seus filtros ou realizar uma nova busca.
                            </p>
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="mt-8 flex justify-center">
                        <Pagination
                            currentPage={paginaAtual}
                            totalPages={totalPaginas}
                            onPageChange={setPaginaAtual}
                        />
                    </div>
                </div>
            </div>
        </SectionLayout>
    )
}

export default ExplorerPage
